import { useEffect, useState } from 'react';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { OptionChips } from '@verlo/shared/components/ui/segmented';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@verlo/shared/components/ui/dialog';

// What this kitchen screen shows (M2, Oct 2026): a station, or the Pass. A manager's call,
// made here on the screen; the page asks for a manager's PIN when the person signed in isn't
// one. The back office lists every screen and what it shows.

const PASS = 'kitchen';
const labelOf = (key, stations) => (key === PASS ? 'The Pass' : (stations.find((s) => s.key === key)?.name || key));
// A screen's name is its station's ("Grill screen") unless someone gave it their own. Any
// station's own name counts as none: the screen may be showing another station by its address.
const ownName = (name, stations) => {
  const defaults = new Set([...stations.map((s) => `${s.name} screen`), 'The Pass screen']);
  return name && !defaults.has(name) ? name : '';
};

const ScreenStationDialog = ({ open, onOpenChange, stations, current, currentName, onSave, busy }) => {
  const [station, setStation] = useState(current || PASS);
  // Only a name someone typed is kept across a change of station: "Larder screen" doesn't
  // follow the screen to the Grill (it used to, as the box opened with the old name in it).
  const [name, setName] = useState(ownName(currentName, stations));
  useEffect(() => {
    if (open) { setStation(current || PASS); setName(ownName(currentName, stations)); }
  }, [open, current, currentName]); // eslint-disable-line react-hooks/exhaustive-deps
  const label = labelOf(station, stations);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="form" data-testid="kds-screen-dialog">
        <DialogHeader>
          <DialogTitle>What this screen shows</DialogTitle>
          <DialogDescription>
            A station sees only the dishes it makes, a tap each. The Pass sees every ticket. The screen keeps this until a manager changes it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <OptionChips
            value={station}
            onChange={setStation}
            // In the order food moves through the kitchen; the Pass, where every dish ends, last.
            options={[...stations.map((s) => ({ value: s.key, label: s.name, testId: `kds-screen-${s.key}` })),
              { value: PASS, label: 'The Pass', testId: 'kds-screen-kitchen' }]}
          />
          <label className="grid gap-1">
            <span className="text-[12px] font-medium text-ink-soft">Name, so the back office can tell the screens apart. Leave it blank for &ldquo;{label} screen&rdquo;.</span>
            <Input value={name} maxLength={40} placeholder={`${label} screen`} onChange={(e) => setName(e.target.value)} data-testid="kds-screen-name" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-verlo hover:bg-verlo-hover" disabled={busy} onClick={() => onSave(station, name.trim() || `${label} screen`)} data-testid="kds-screen-save">
            Show {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScreenStationDialog;
